import { D2041_READING_FIELDS, D2726_READING_FIELDS, ROW_STATUS, averageSpecificGravity, evaluateD2041Determination, evaluateD2726Specimen, } from './asphalt-specific-gravity.js';
import { T85_READING_FIELDS, T96_FRACTION_KEYS, averageT85, evaluateT85Determination, evaluateT96, } from './coarse-aggregate.js';
import { MOISTURE_DETERMINATION_STATUS, MOISTURE_ISSUE_CODE, MOISTURE_READING_FIELDS, READING_SCALE, evaluateMoistureDetermination, parseReading, } from './moisture-determination.js';
/**
 * SPECIALISED RESULT TEMPLATES — the implemented ones, by key (D238).
 *
 * A catalogue item names its template by one of these keys in
 * `resultTemplateVersion`, and may be marked `ready` only when it does. Every
 * other test uses the general path (CLAUDE.md section 4). The key names the
 * template's VERSION: a future change to the D2216 form is a new key, and an
 * attempt keeps the key it started with (D239).
 */
export const RESULT_TEMPLATE = Object.freeze({
    /** ASTM D2216 — water (moisture) content of soil and rock by mass (D237). */
    ASTM_D2216_V1: 'astm_d2216_v1',
    /** ASTM D2726 — bulk specific gravity (Gmb) of compacted asphalt mixtures (D260). */
    ASTM_D2726_V1: 'astm_d2726_v1',
    /** ASTM D2041 — theoretical maximum specific gravity (Gmm) of asphalt mixtures (D260). */
    ASTM_D2041_V1: 'astm_d2041_v1',
    /** AASHTO T85 — specific gravity and absorption of coarse aggregate (D267). */
    AASHTO_T85_V1: 'aashto_t85_v1',
    /** AASHTO T96 — resistance to degradation by Los Angeles abrasion (D267). */
    AASHTO_T96_V1: 'aashto_t96_v1',
});
export const IMPLEMENTED_RESULT_TEMPLATES = Object.freeze(Object.values(RESULT_TEMPLATE));
export function isImplementedResultTemplate(key) {
    return typeof key === 'string' && IMPLEMENTED_RESULT_TEMPLATES.includes(key);
}
// ---------------------------------------------------------------------------
// ASTM D2216, version 1 — every field traced to `SOIL-ASTM D2216 Rev.01.xlsx`,
// sheet `Moisture` (PROJECT_DOCUMENTATION section 15.42)
// ---------------------------------------------------------------------------
/** Method A or B (D16; the methods named in N28 and Q28). */
export const D2216_METHODS = Object.freeze(['A', 'B']);
/** The instruments the workbook lists (B29 Balance, B30 Oven). */
export const D2216_EQUIPMENT_ROLES = Object.freeze(['balance', 'oven']);
/** The instruments F03/F04 sheet `3` lists (A26:A29): balance, oven, thermometer, wire basket. */
export const T85_EQUIPMENT_ROLES = Object.freeze(['balance', 'oven', 'thermometer', 'wire_basket']);
/** The instruments F03/F04 sheet `7` lists (AR26:AR28): L. A. machine, balance, sieve. */
export const T96_EQUIPMENT_ROLES = Object.freeze(['la_machine', 'balance', 'sieve']);
/** Every equipment role any implemented template uses. */
export const ALL_EQUIPMENT_ROLES = Object.freeze([
    ...new Set([...D2216_EQUIPMENT_ROLES, ...T85_EQUIPMENT_ROLES, ...T96_EQUIPMENT_ROLES]),
]);
/**
 * The equipment roles a template names, each required at submission; none on
 * the general path. The asphalt workbook names no instrument, so D2726 and
 * D2041 record register equipment without a role (D263).
 */
export function templateEquipmentRoles(key) {
    switch (key) {
        case RESULT_TEMPLATE.ASTM_D2216_V1:
            return D2216_EQUIPMENT_ROLES;
        case RESULT_TEMPLATE.AASHTO_T85_V1:
            return T85_EQUIPMENT_ROLES;
        case RESULT_TEMPLATE.AASHTO_T96_V1:
            return T96_EQUIPMENT_ROLES;
        default:
            return [];
    }
}
export const D2216_SCHEMA_VERSION = 1;
export const D2216_ISSUE_CODE = Object.freeze({
    DETERMINATIONS_REQUIRED: 'determinations_required',
    EQUIPMENT_ROLE_REQUIRED: 'equipment_role_required',
    OUT_OF_RANGE: 'out_of_range',
});
/** An empty D2216 document — what a new attempt starts with. */
export function emptyD2216Document() {
    return {
        schemaVersion: D2216_SCHEMA_VERSION,
        testInformation: {
            methodUsed: null,
            ovenTemperature: null,
            ovenStartedAt: null,
            nominalMaximumSize: null,
        },
        determinations: [],
        environment: { ambientTemperature: null, relativeHumidity: null },
        remarks: null,
    };
}
/** Text that is blank is absent — never zero (CLAUDE.md section 8). */
function present(value) {
    if (value === null || value === undefined)
        return null;
    return value.trim() === '' ? null : value.trim();
}
/**
 * What even a DRAFT refuses: text in a numeric field that is not a number.
 * Everything else — a missing reading, an impossible pair — is saved and
 * flagged, and refused only at submission (D242).
 */
export function d2216DraftProblems(input) {
    const problems = [];
    input.determinations.forEach((row, index) => {
        for (const field of MOISTURE_READING_FIELDS) {
            if (parseReading(row[field]).kind === 'invalid') {
                problems.push({ field: `determinations.${index}.${field}`, code: MOISTURE_ISSUE_CODE.NOT_A_DECIMAL });
            }
        }
    });
    for (const field of ['ambientTemperature', 'relativeHumidity']) {
        if (parseReading(input.environment[field]).kind === 'invalid') {
            problems.push({ field: `environment.${field}`, code: MOISTURE_ISSUE_CODE.NOT_A_DECIMAL });
        }
    }
    return problems;
}
/**
 * Build the stored document from what was sent: readings exactly as typed
 * (trimmed; blank is absent), and each row's calculation by the shared
 * moisture component. The caller has already refused `d2216DraftProblems`.
 */
export function buildD2216Document(input) {
    return {
        schemaVersion: D2216_SCHEMA_VERSION,
        testInformation: {
            methodUsed: input.testInformation.methodUsed ?? null,
            ovenTemperature: present(input.testInformation.ovenTemperature),
            ovenStartedAt: input.testInformation.ovenStartedAt ?? null,
            nominalMaximumSize: present(input.testInformation.nominalMaximumSize),
        },
        determinations: input.determinations.map((row) => {
            const readings = {
                containerMass: present(row.containerMass),
                containerWetMass: present(row.containerWetMass),
                containerDryMass: present(row.containerDryMass),
            };
            const evaluation = evaluateMoistureDetermination(readings);
            return {
                id: row.id,
                containerNumber: present(row.containerNumber),
                ...readings,
                status: evaluation.status,
                calculation: evaluation.calculation,
            };
        }),
        environment: {
            ambientTemperature: present(input.environment.ambientTemperature),
            relativeHumidity: present(input.environment.relativeHumidity),
        },
        remarks: present(input.remarks),
    };
}
/**
 * What stops a D2216 draft being submitted. Every gap at once, each naming
 * its exact field. `equipmentRoles` are the roles of the instruments recorded
 * on the attempt and not removed.
 */
export function d2216SubmissionGaps(document, execution, equipmentRoles) {
    const gaps = [];
    if (!execution.executionStartedAt)
        gaps.push({ field: 'executionStartedAt', code: 'required' });
    if (!execution.executionCompletedAt)
        gaps.push({ field: 'executionCompletedAt', code: 'required' });
    if (!document.testInformation.methodUsed) {
        gaps.push({ field: 'testInformation.methodUsed', code: 'required' });
    }
    if (document.determinations.length === 0) {
        gaps.push({ field: 'determinations', code: D2216_ISSUE_CODE.DETERMINATIONS_REQUIRED });
    }
    document.determinations.forEach((row, index) => {
        if (row.status === MOISTURE_DETERMINATION_STATUS.COMPLETE)
            return;
        for (const issue of evaluateMoistureDetermination(row).issues) {
            gaps.push({ field: `determinations.${index}.${issue.field}`, code: issue.code });
        }
    });
    const humidity = parseReading(document.environment.relativeHumidity);
    // Relative humidity is a percentage of saturation by definition.
    if (humidity.kind === 'value' && (humidity.scaled < 0n || humidity.scaled > 100n * 10n ** BigInt(READING_SCALE))) {
        gaps.push({ field: 'environment.relativeHumidity', code: D2216_ISSUE_CODE.OUT_OF_RANGE });
    }
    for (const role of D2216_EQUIPMENT_ROLES) {
        if (!equipmentRoles.includes(role)) {
            gaps.push({ field: `equipment.${role}`, code: D2216_ISSUE_CODE.EQUIPMENT_ROLE_REQUIRED });
        }
    }
    return gaps;
}
function environmentDraftProblems(environment) {
    const problems = [];
    for (const field of ['ambientTemperature', 'relativeHumidity']) {
        if (parseReading(environment[field]).kind === 'invalid') {
            problems.push({ field: `environment.${field}`, code: MOISTURE_ISSUE_CODE.NOT_A_DECIMAL });
        }
    }
    return problems;
}
function environmentGaps(environment) {
    const humidity = parseReading(environment.relativeHumidity);
    // Relative humidity is a percentage of saturation by definition.
    return humidity.kind === 'value' &&
        (humidity.scaled < 0n || humidity.scaled > 100n * 10n ** BigInt(READING_SCALE))
        ? [{ field: 'environment.relativeHumidity', code: D2216_ISSUE_CODE.OUT_OF_RANGE }]
        : [];
}
function buildEnvironment(environment) {
    return {
        ambientTemperature: present(environment.ambientTemperature),
        relativeHumidity: present(environment.relativeHumidity),
    };
}
function executionGaps(execution) {
    const gaps = [];
    if (!execution.executionStartedAt)
        gaps.push({ field: 'executionStartedAt', code: 'required' });
    if (!execution.executionCompletedAt)
        gaps.push({ field: 'executionCompletedAt', code: 'required' });
    return gaps;
}
/** Text a numeric reading field holds that is not a number, per row. */
function rowDraftProblems(rowsField, rows, fields) {
    const problems = [];
    rows.forEach((row, index) => {
        for (const field of fields) {
            if (parseReading(row[field]).kind === 'invalid') {
                problems.push({ field: `${rowsField}.${index}.${field}`, code: MOISTURE_ISSUE_CODE.NOT_A_DECIMAL });
            }
        }
    });
    return problems;
}
// ---------------------------------------------------------------------------
// ASTM D2726, version 1 — traced to `asphalt.xlsx`, sheet `2`, A15:AH24
// (docs/REFERENCE_TEST_AUDIT.md 3.17; decisions D260–D266)
// ---------------------------------------------------------------------------
export const D2726_SCHEMA_VERSION = 1;
/** The workbook lays out three specimen columns (Q, W, AC). */
export const D2726_MAX_SPECIMENS = 3;
export function emptyD2726Document() {
    return {
        schemaVersion: D2726_SCHEMA_VERSION,
        templateKey: RESULT_TEMPLATE.ASTM_D2726_V1,
        testInformation: { methodUsed: null },
        specimens: [],
        summary: { averageBulkSpecificGravity: null },
        environment: { ambientTemperature: null, relativeHumidity: null },
        remarks: null,
    };
}
export function d2726DraftProblems(input) {
    return [
        ...rowDraftProblems('specimens', input.specimens, D2726_READING_FIELDS),
        ...environmentDraftProblems(input.environment),
    ];
}
export function buildD2726Document(input) {
    const specimens = input.specimens.map((row) => {
        const readings = {
            dryMassInAir: present(row.dryMassInAir),
            ssdMassInAir: present(row.ssdMassInAir),
            massInWater: present(row.massInWater),
        };
        const evaluation = evaluateD2726Specimen(readings);
        return {
            id: row.id,
            specimenNumber: present(row.specimenNumber),
            ...readings,
            status: evaluation.status,
            calculation: evaluation.calculation,
        };
    });
    return {
        schemaVersion: D2726_SCHEMA_VERSION,
        templateKey: RESULT_TEMPLATE.ASTM_D2726_V1,
        testInformation: { methodUsed: present(input.testInformation.methodUsed) },
        specimens,
        summary: {
            averageBulkSpecificGravity: averageSpecificGravity(specimens.map((row) => row.calculation?.bulkSpecificGravity)),
        },
        environment: buildEnvironment(input.environment),
        remarks: present(input.remarks),
    };
}
/**
 * What stops a D2726 draft being submitted, all at once. The workbook names
 * no instrument, so no equipment role is required (D263).
 */
export function d2726SubmissionGaps(document, execution) {
    const gaps = executionGaps(execution);
    if (document.specimens.length === 0) {
        gaps.push({ field: 'specimens', code: D2216_ISSUE_CODE.DETERMINATIONS_REQUIRED });
    }
    if (document.specimens.length > D2726_MAX_SPECIMENS) {
        gaps.push({ field: 'specimens', code: TEMPLATE_ISSUE_CODE.TOO_MANY_ROWS });
    }
    document.specimens.forEach((row, index) => {
        if (row.status === ROW_STATUS.COMPLETE)
            return;
        for (const issue of evaluateD2726Specimen(row).issues) {
            gaps.push({ field: `specimens.${index}.${issue.field}`, code: issue.code });
        }
    });
    return [...gaps, ...environmentGaps(document.environment)];
}
// ---------------------------------------------------------------------------
// ASTM D2041, version 1 — traced to `asphalt.xlsx`, sheet `2`, AI15:BP24
// (docs/REFERENCE_TEST_AUDIT.md 3.18; decisions D260–D266)
// ---------------------------------------------------------------------------
export const D2041_SCHEMA_VERSION = 1;
/** The workbook lays out two determination columns (BE, BK). */
export const D2041_MAX_DETERMINATIONS = 2;
export function emptyD2041Document() {
    return {
        schemaVersion: D2041_SCHEMA_VERSION,
        templateKey: RESULT_TEMPLATE.ASTM_D2041_V1,
        testInformation: { containerType: null, procedure: null },
        determinations: [],
        summary: { averageMaximumSpecificGravity: null },
        environment: { ambientTemperature: null, relativeHumidity: null },
        remarks: null,
    };
}
export function d2041DraftProblems(input) {
    return [
        ...rowDraftProblems('determinations', input.determinations, D2041_READING_FIELDS),
        ...environmentDraftProblems(input.environment),
    ];
}
export function buildD2041Document(input) {
    const determinations = input.determinations.map((row) => {
        const readings = {
            dryMassInAir: present(row.dryMassInAir),
            containerWaterMass: present(row.containerWaterMass),
            containerSampleWaterMass: present(row.containerSampleWaterMass),
        };
        const evaluation = evaluateD2041Determination(readings);
        return {
            id: row.id,
            specimenNumber: present(row.specimenNumber),
            ...readings,
            status: evaluation.status,
            calculation: evaluation.calculation,
        };
    });
    return {
        schemaVersion: D2041_SCHEMA_VERSION,
        templateKey: RESULT_TEMPLATE.ASTM_D2041_V1,
        testInformation: {
            containerType: present(input.testInformation.containerType),
            procedure: present(input.testInformation.procedure),
        },
        determinations,
        summary: {
            averageMaximumSpecificGravity: averageSpecificGravity(determinations.map((row) => row.calculation?.maximumSpecificGravity)),
        },
        environment: buildEnvironment(input.environment),
        remarks: present(input.remarks),
    };
}
/** What stops a D2041 draft being submitted, all at once (no role required, D263). */
export function d2041SubmissionGaps(document, execution) {
    const gaps = executionGaps(execution);
    if (document.determinations.length === 0) {
        gaps.push({ field: 'determinations', code: D2216_ISSUE_CODE.DETERMINATIONS_REQUIRED });
    }
    if (document.determinations.length > D2041_MAX_DETERMINATIONS) {
        gaps.push({ field: 'determinations', code: TEMPLATE_ISSUE_CODE.TOO_MANY_ROWS });
    }
    document.determinations.forEach((row, index) => {
        if (row.status === ROW_STATUS.COMPLETE)
            return;
        for (const issue of evaluateD2041Determination(row).issues) {
            gaps.push({ field: `determinations.${index}.${issue.field}`, code: issue.code });
        }
    });
    return [...gaps, ...environmentGaps(document.environment)];
}
export const TEMPLATE_ISSUE_CODE = Object.freeze({
    /** More rows than the template's workbook lays out. */
    TOO_MANY_ROWS: 'too_many_rows',
    /** A count (spheres, revolutions) that is not a whole number above zero. */
    NOT_A_POSITIVE_WHOLE_NUMBER: 'not_a_positive_whole_number',
});
/** Each required role without an instrument recorded for it. */
function roleGaps(required, recorded) {
    return required
        .filter((role) => !recorded.includes(role))
        .map((role) => ({ field: `equipment.${role}`, code: D2216_ISSUE_CODE.EQUIPMENT_ROLE_REQUIRED }));
}
// ---------------------------------------------------------------------------
// AASHTO T85, version 1 — traced to the aggregate workbooks F03/F04, sheet `3`
// (docs/REFERENCE_TEST_AUDIT.md 3.8; decisions D267–D274). The sheet records
// no ambient conditions and no test information beyond the dates.
// ---------------------------------------------------------------------------
export const T85_SCHEMA_VERSION = 1;
/** The workbook lays out three determination columns (AC, AM, AW). */
export const T85_MAX_DETERMINATIONS = 3;
export function emptyT85Document() {
    return {
        schemaVersion: T85_SCHEMA_VERSION,
        templateKey: RESULT_TEMPLATE.AASHTO_T85_V1,
        determinations: [],
        summary: averageT85([]),
        remarks: null,
    };
}
export function t85DraftProblems(input) {
    return rowDraftProblems('determinations', input.determinations, T85_READING_FIELDS);
}
export function buildT85Document(input) {
    const determinations = input.determinations.map((row) => {
        const readings = {
            dryMassInAir: present(row.dryMassInAir),
            ssdMassInAir: present(row.ssdMassInAir),
            massInWater: present(row.massInWater),
        };
        const evaluation = evaluateT85Determination(readings);
        return { id: row.id, ...readings, status: evaluation.status, calculation: evaluation.calculation };
    });
    return {
        schemaVersion: T85_SCHEMA_VERSION,
        templateKey: RESULT_TEMPLATE.AASHTO_T85_V1,
        determinations,
        summary: averageT85(determinations.map((row) => row.calculation)),
        remarks: present(input.remarks),
    };
}
/** What stops a T85 draft being submitted, all at once. */
export function t85SubmissionGaps(document, execution, equipmentRoles) {
    const gaps = executionGaps(execution);
    if (document.determinations.length === 0) {
        gaps.push({ field: 'determinations', code: D2216_ISSUE_CODE.DETERMINATIONS_REQUIRED });
    }
    if (document.determinations.length > T85_MAX_DETERMINATIONS) {
        gaps.push({ field: 'determinations', code: TEMPLATE_ISSUE_CODE.TOO_MANY_ROWS });
    }
    document.determinations.forEach((row, index) => {
        if (row.status === ROW_STATUS.COMPLETE)
            return;
        for (const issue of evaluateT85Determination(row).issues) {
            gaps.push({ field: `determinations.${index}.${issue.field}`, code: issue.code });
        }
    });
    return [...gaps, ...roleGaps(T85_EQUIPMENT_ROLES, equipmentRoles)];
}
// ---------------------------------------------------------------------------
// AASHTO T96, version 1 — traced to the aggregate workbooks F03/F04, sheet `7`
// (docs/REFERENCE_TEST_AUDIT.md 3.12; decisions D267–D274). The sheet records
// no ambient conditions.
// ---------------------------------------------------------------------------
export const T96_SCHEMA_VERSION = 1;
const T96_COUNT_FIELDS = ['numberOfSpheres', 'revolutions'];
function emptyGrading() {
    return Object.fromEntries(T96_FRACTION_KEYS.map((key) => [key, null]));
}
export function emptyT96Document() {
    return {
        schemaVersion: T96_SCHEMA_VERSION,
        templateKey: RESULT_TEMPLATE.AASHTO_T96_V1,
        testInformation: { gradingDesignation: null, numberOfSpheres: null, sphereMass: null, revolutions: null },
        grading: emptyGrading(),
        retainedMassNo12: null,
        status: ROW_STATUS.INCOMPLETE,
        calculation: null,
        remarks: null,
    };
}
export function t96DraftProblems(input) {
    const numeric = [
        ...T96_FRACTION_KEYS.map((key) => [`grading.${key}`, input.grading[key]]),
        ['retainedMassNo12', input.retainedMassNo12],
        ['testInformation.numberOfSpheres', input.testInformation.numberOfSpheres],
        ['testInformation.sphereMass', input.testInformation.sphereMass],
        ['testInformation.revolutions', input.testInformation.revolutions],
    ];
    return numeric
        .filter(([, value]) => parseReading(value).kind === 'invalid')
        .map(([field]) => ({ field, code: MOISTURE_ISSUE_CODE.NOT_A_DECIMAL }));
}
export function buildT96Document(input) {
    const grading = Object.fromEntries(T96_FRACTION_KEYS.map((key) => [key, present(input.grading[key])]));
    const retainedMassNo12 = present(input.retainedMassNo12);
    const evaluation = evaluateT96({ grading, retainedMassNo12 });
    return {
        schemaVersion: T96_SCHEMA_VERSION,
        templateKey: RESULT_TEMPLATE.AASHTO_T96_V1,
        testInformation: {
            gradingDesignation: present(input.testInformation.gradingDesignation),
            numberOfSpheres: present(input.testInformation.numberOfSpheres),
            sphereMass: present(input.testInformation.sphereMass),
            revolutions: present(input.testInformation.revolutions),
        },
        grading,
        retainedMassNo12,
        status: evaluation.status,
        calculation: evaluation.calculation,
        remarks: present(input.remarks),
    };
}
/**
 * What stops a T96 draft being submitted, all at once: every test detail the
 * sheet prints, at least one fraction mass, the retained mass, and each
 * instrument. The grading table is NOT checked against the grading
 * designation — the workbook does not establish that rule (D271).
 */
export function t96SubmissionGaps(document, execution, equipmentRoles) {
    const gaps = executionGaps(execution);
    const info = document.testInformation;
    if (!info.gradingDesignation)
        gaps.push({ field: 'testInformation.gradingDesignation', code: 'required' });
    for (const field of T96_COUNT_FIELDS) {
        const parsed = parseReading(info[field]);
        if (parsed.kind === 'missing') {
            gaps.push({ field: `testInformation.${field}`, code: MOISTURE_ISSUE_CODE.READING_REQUIRED });
        }
        else if (parsed.kind === 'invalid' ||
            parsed.scaled <= 0n ||
            parsed.scaled % 10n ** BigInt(READING_SCALE) !== 0n) {
            gaps.push({ field: `testInformation.${field}`, code: TEMPLATE_ISSUE_CODE.NOT_A_POSITIVE_WHOLE_NUMBER });
        }
    }
    const sphereMass = parseReading(info.sphereMass);
    if (sphereMass.kind === 'missing') {
        gaps.push({ field: 'testInformation.sphereMass', code: MOISTURE_ISSUE_CODE.READING_REQUIRED });
    }
    else if (sphereMass.kind === 'invalid') {
        gaps.push({ field: 'testInformation.sphereMass', code: MOISTURE_ISSUE_CODE.NOT_A_DECIMAL });
    }
    else if (sphereMass.scaled < 0n) {
        gaps.push({ field: 'testInformation.sphereMass', code: MOISTURE_ISSUE_CODE.NEGATIVE_MASS });
    }
    if (document.status !== ROW_STATUS.COMPLETE) {
        for (const issue of evaluateT96(document).issues)
            gaps.push({ field: issue.field, code: issue.code });
    }
    return [...gaps, ...roleGaps(T96_EQUIPMENT_ROLES, equipmentRoles)];
}
function keyOf(document) {
    return document ? document.templateKey : undefined;
}
export function isD2726Document(document) {
    return Boolean(document) && document.templateKey === RESULT_TEMPLATE.ASTM_D2726_V1;
}
export function isD2041Document(document) {
    return Boolean(document) && document.templateKey === RESULT_TEMPLATE.ASTM_D2041_V1;
}
export function isT85Document(document) {
    return keyOf(document) === RESULT_TEMPLATE.AASHTO_T85_V1;
}
export function isT96Document(document) {
    return keyOf(document) === RESULT_TEMPLATE.AASHTO_T96_V1;
}
/** A D2216 document is the one that carries no template key (D261). */
export function isD2216Document(document) {
    return Boolean(document) && keyOf(document) === undefined;
}
/**
 * The repeating rows of any structured document, with their statuses. T96 has
 * one grading table rather than rows, so it reports that table as one row.
 */
export function structuredRows(document) {
    if (isD2726Document(document))
        return document.specimens;
    if (isT96Document(document))
        return [{ id: 'grading', status: document.status }];
    return document.determinations;
}
//# sourceMappingURL=result-templates.js.map